import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { NotificationsFilters } from '@finxone-platform/form-action';
import {
  CreateNewSupportChatResponse,
  PaginatedResponse,
  SupportChatSubject,
  SupportConversation,
  SupportMessagesResponse,
  SupportMessageType,
  ThreadStatus,
} from '@finxone-platform/shared/sys-config-types';
import { Observable, take } from 'rxjs';
import { ConfigService } from '../config-service/config-service.service';
import { buildQueryString } from '@app/finxone-web-frontend/app/lib/utils/query-params.utils';

export type NewChatData = {
  content: string;
  subjectId: string;
  accountId?: string;
};

@Injectable({
  providedIn: 'root',
})
export class SupportChatsService {
  private baseUrl = '';
  public activeChatId = signal<string | null>(null);
  public activeChatStatus = signal<string | ThreadStatus | null>(ThreadStatus.OPEN);
  public isNewChat = signal<boolean>(false);
  public welcomeMessage = signal<string | null>(null);

  constructor(private readonly configService: ConfigService, private readonly http: HttpClient) {
    this.configService
      .getApi('messaging_service')
      .pipe(take(1))
      .subscribe((apiUrl) => {
        this.baseUrl = apiUrl;
      });
  }

  public getSupportChats(
    page = 1,
    limit = 10,
    filter = NotificationsFilters.All,
    search?: string,
  ): Observable<PaginatedResponse<SupportConversation>> {
    return this.http.get<PaginatedResponse<SupportConversation>>(
      `${this.baseUrl}/messaging/threads?page=${page}&limit=${limit}${
        search ? `&search=${search}` : ''
      }&filter=${filter}`,
    );
  }

  public getSupportChatSubjects(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      isActive?: boolean;
      assignedGroupId?: string;
    } = {},
  ): Observable<PaginatedResponse<SupportChatSubject>> {
    const query = buildQueryString(params);
    return this.http.get<PaginatedResponse<SupportChatSubject>>(
      `${this.baseUrl}/messaging/subjects${query ? `?${query}` : ''}`,
    );
  }

  public markSupportThreadAsRead(threadId: string, lastReadMessageId: string) {
    return this.http.post(`${this.baseUrl}/messaging/threads/${threadId}/read`, {
      lastReadMessageId,
    });
  }

  public setActiveChatStatus(status: string | ThreadStatus | null) {
    this.activeChatStatus.set(status);
  }

  public setActiveChatId(id: string) {
    this.activeChatId.set(id);
  }

  public resetActiveChatId() {
    this.activeChatId.set(null);
  }

  public setNewChatStatus(isNew: boolean) {
    this.isNewChat.set(isNew);
  }

  public setWelcomeMessage(text: string | null) {
    this.welcomeMessage.set(text);
  }

  public getSupportChatMessages(
    chatId: string,
    limit = 50,
    before?: string,
  ): Observable<SupportMessagesResponse> {
    return this.http.get<SupportMessagesResponse>(
      `${this.baseUrl}/messaging/threads/${chatId}/messages?limit=${limit}${
        before ? `&before=${before}` : ''
      }`,
    );
  }

  public createNewSupportConversation(newChatData: NewChatData): Observable<CreateNewSupportChatResponse> {
    return this.http.post<CreateNewSupportChatResponse>(`${this.baseUrl}/messaging/threads/`, newChatData);
  }

  public sendMessageOnExistingConversation(
    threadId: string,
    message: string,
  ): Observable<SupportMessageType> {
    return this.http.post<SupportMessageType>(`${this.baseUrl}/messaging/threads/${threadId}/messages`, {
      content: message,
    });
  }

  public getSupportChatMessageDefault(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/messaging/message-defaults/welcome`);
  }
}
